// Horizontal Slider Animation (ES Module)
// Uses global gsap, ScrollTrigger, and Lenis from window object

const config = {
     SCROLL_SPEED: 1.75,
     LERP_FACTOR: 0.05,
     MAX_VELOCITY: 150,
};

let sliderData = [];
let totalSlideCount = 0;

const state = {
     currentX: 0,
     targetX: 0,
     slideWidth: 390,
     slides: [],
     isDragging: false,
     startX: 0,
     lastX: 0,
     lastMouseX: 0,
     lastScrollTime: Date.now(),
     isMoving: false,
     velocity: 0,
     lastCurrentX: 0,
     dragDistance: 0,
     hasActuallyDragged: false,
     isMobile: false,
};

function getThemeBasedFilters() {
     try {
          const currentTheme = document.documentElement.getAttribute('data-theme') ||
               localStorage.getItem('theme') || 'light';

          if (currentTheme === 'nightmare') {
               return { include: ['Nightmare_'], exclude: [] };
          } else {
               return { include: [], exclude: ['Nightmare_'] };
          }
     } catch (e) {
          console.warn('Theme-based filtering error:', e);
          return { include: [], exclude: [] };
     }
}

function fileMatchesPrefixes(filename, include, exclude) {
     try {
          const lastDot = filename.lastIndexOf('.');
          const name = lastDot > 0 ? filename.slice(0, lastDot) : filename;
          if (include.length > 0 && !include.some(s => name.startsWith(s))) {
               return false;
          }
          if (exclude.length > 0 && exclude.some(s => name.startsWith(s))) {
               return false;
          }
          return true;
     } catch (_) {
          return true;
     }
}

function filterImagesByPrefixes(images, filters) {
     if (!filters || (!filters.include.length && !filters.exclude.length)) return images;
     const filtered = images.filter(img => fileMatchesPrefixes(img, filters.include, filters.exclude));
     return filtered.length ? filtered : images;
}

async function loadMatelotDubeImages() {
     try {
          const response = await fetch('/assets/img/SDGDR/matelot_dube.json');
          const data = await response.json();

          const filters = getThemeBasedFilters();
          const allImages = data.images || [];
          const normalized = allImages.map(i => typeof i === 'string' ? { filename: i } : i);
          const matelotImages = filterImagesByPrefixes(normalized, filters);

          const shuffled = [...matelotImages].sort(() => 0.5 - Math.random());
          const selectedEntries = shuffled.slice(0, Math.min(12, matelotImages.length));

          const support = await (async () => {
               const tests = { avif: "data:image/avif;base64,AAAAIGZ0eXBhdmlmAAAAAG1pZjFhdmlmAAAA", webp: "data:image/webp;base64,UklGRiIAAABXRUJQVlA4TBEAAAAvAAAAAAfQ//73v/+BiOh/AAA=" };
               const check = src => new Promise(resolve => { const img = new Image(); img.onload = () => resolve(true); img.onerror = () => resolve(false); img.src = src; });
               return { avif: await check(tests.avif), webp: await check(tests.webp) };
          })();
          const preferred = support.avif ? 'avif' : (support.webp ? 'webp' : 'jpeg');

          const vw = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
          const chosenSize = vw <= 420 ? 400 : (vw <= 900 ? 800 : 1200);

          const getOptimizedPath = (entry) => {
               if (entry.variants && entry.variants[preferred] && entry.variants[preferred][String(chosenSize)]) {
                    return entry.variants[preferred][String(chosenSize)];
               }
               if (entry.variants) {
                    for (const fmt of ['webp', 'jpeg', 'avif']) {
                         if (entry.variants[fmt] && entry.variants[fmt][String(chosenSize)]) {
                              return entry.variants[fmt][String(chosenSize)];
                         }
                    }
                    for (const fmt of ['webp', 'jpeg', 'avif']) {
                         if (entry.variants && entry.variants[fmt]) {
                              const sizes = Object.keys(entry.variants[fmt]).map(k => Number(k)).sort((a, b) => a - b);
                              return entry.variants[fmt][String(sizes[0])];
                         }
                    }
               }
               const filename = entry.filename || (typeof entry === 'string' ? entry : '');
               return `/assets/img/SDGDR/Matelot Dube/${filename}`;
          };

          if (selectedEntries.length === 0) {
               console.warn('🚨 No Matelot Dube images available after filtering, using fallback');
               sliderData = [
                    {
                         title: "Matelot Dube",
                         img: "/assets/img/SDGDR/Matelot Dube/optimized/ComfyUI_00954_-800.webp",
                         url: "#",
                    }
               ];
          } else {
               sliderData = selectedEntries.map((entry, index) => ({
                    title: `Matelot Dube ${index + 1}`,
                    img: getOptimizedPath(entry),
                    url: "#",
               }));
          }

          totalSlideCount = sliderData.length;

          if (totalSlideCount < 4) {
               const originalCount = totalSlideCount;
               while (totalSlideCount < 8) {
                    const duplicateIndex = totalSlideCount % originalCount;
                    sliderData.push({
                         title: `Matelot Dube ${totalSlideCount + 1}`,
                         img: sliderData[duplicateIndex].img,
                         url: "#",
                    });
                    totalSlideCount++;
               }
          }

          console.log(`🎯 Loaded ${selectedEntries.length} Matelot Dube images for slider`);
     } catch (error) {
          console.warn('Could not load Matelot Dube images:', error);
          sliderData = [
               {
                    title: "Matelot Dube 1",
                    img: "/assets/img/SDGDR/Matelot Dube/optimized/ComfyUI_00954_-800.webp",
                    url: "#",
               },
               {
                    title: "Matelot Dube 2",
                    img: "/assets/img/SDGDR/Matelot Dube/optimized/ComfyUI_00955_-800.webp",
                    url: "#",
               },
               {
                    title: "Matelot Dube 3",
                    img: "/assets/img/SDGDR/Matelot Dube/optimized/ComfyUI_00956_-800.webp",
                    url: "#",
               },
          ];
          totalSlideCount = sliderData.length;
     }
}

function checkMobile() {
     state.isMobile = window.innerWidth < 1000;
}

function createSlideElement(index) {
     const slide = document.createElement("div");
     slide.className = "slide";

     if (state.isMobile) {
          slide.style.width = "175px";
          slide.style.height = "250px";
     }

     const imageContainer = document.createElement("div");
     imageContainer.className = "slide-image";

     const img = document.createElement("img");
     const dataIndex = index % totalSlideCount;
     img.src = sliderData[dataIndex].img;
     img.alt = sliderData[dataIndex].title;

     const overlay = document.createElement("div");
     overlay.className = "slide-overlay";

     const title = document.createElement("p");
     title.className = "project-title";
     title.textContent = sliderData[dataIndex].title;

     const arrow = document.createElement("div");
     arrow.className = "project-arrow";
     arrow.innerHTML = `
    <svg viewBox="0 0 24 24">
      <path d="M7 17L17 7M17 7H7M17 7V17"/>
    </svg>
  `;

     slide.addEventListener("click", (e) => {
          e.preventDefault();
          if (state.dragDistance < 10 && !state.hasActuallyDragged) {
               window.location.href = sliderData[dataIndex].url;
          }
     });

     overlay.appendChild(title);
     overlay.appendChild(arrow);
     imageContainer.appendChild(img);
     slide.appendChild(imageContainer);
     slide.appendChild(overlay);

     return slide;
}

function initializeSlides() {
     const track = document.querySelector(".slide-track");
     if (!track) return;
     
     track.innerHTML = "";
     state.slides = [];

     checkMobile();
     state.slideWidth = state.isMobile ? 215 : 390;

     const copies = 6;
     const totalSlides = totalSlideCount * copies;

     for (let i = 0; i < totalSlides; i++) {
          const slide = createSlideElement(i);
          track.appendChild(slide);
          state.slides.push(slide);
     }

     const startOffset = -(totalSlideCount * state.slideWidth * 2);
     state.currentX = startOffset;
     state.targetX = startOffset;
}

function updateSlidePositions() {
     const track = document.querySelector(".slide-track");
     if (!track) return;
     
     const sequenceWidth = state.slideWidth * totalSlideCount;

     if (state.currentX > -sequenceWidth * 1) {
          state.currentX -= sequenceWidth;
          state.targetX -= sequenceWidth;
     } else if (state.currentX < -sequenceWidth * 4) {
          state.currentX += sequenceWidth;
          state.targetX += sequenceWidth;
     }

     track.style.transform = `translate3d(${state.currentX}px, 0, 0)`;
}

function updateParallax() {
     const viewportCenter = window.innerWidth / 2;

     state.slides.forEach((slide) => {
          const img = slide.querySelector("img");
          if (!img) return;

          const slideRect = slide.getBoundingClientRect();

          if (slideRect.right < -500 || slideRect.left > window.innerWidth + 500) {
               return;
          }

          const slideCenter = slideRect.left + slideRect.width / 2;
          const distanceFromCenter = slideCenter - viewportCenter;
          const parallaxOffset = distanceFromCenter * -0.25;

          img.style.transform = `translateX(${parallaxOffset}px) scale(2.25)`;
     });
}

function updateMovingState() {
     state.velocity = Math.abs(state.currentX - state.lastCurrentX);
     state.lastCurrentX = state.currentX;

     const isSlowEnough = state.velocity < 0.1;
     const hasBeenStillLongEnough = Date.now() - state.lastScrollTime > 200;
     state.isMoving =
          state.hasActuallyDragged || !isSlowEnough || !hasBeenStillLongEnough;

     document.documentElement.style.setProperty(
          "--slider-moving",
          state.isMoving ? "1" : "0"
     );
}

function animate() {
     state.currentX += (state.targetX - state.currentX) * config.LERP_FACTOR;

     updateMovingState();
     updateSlidePositions();
     updateParallax();

     requestAnimationFrame(animate);
}

function handleWheel(e) {
     if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
          return;
     }

     e.preventDefault();
     state.lastScrollTime = Date.now();

     const scrollDelta = e.deltaY * config.SCROLL_SPEED;
     state.targetX -= Math.max(
          Math.min(scrollDelta, config.MAX_VELOCITY),
          -config.MAX_VELOCITY
     );
}

function handleTouchStart(e) {
     state.isDragging = true;
     state.startX = e.touches[0].clientX;
     state.lastX = state.targetX;
     state.dragDistance = 0;
     state.hasActuallyDragged = false;
     state.lastScrollTime = Date.now();
}

function handleTouchMove(e) {
     if (!state.isDragging) return;

     const deltaX = (e.touches[0].clientX - state.startX) * 1.5;
     state.targetX = state.lastX + deltaX;
     state.dragDistance = Math.abs(deltaX);

     if (state.dragDistance > 5) {
          state.hasActuallyDragged = true;
     }

     state.lastScrollTime = Date.now();
}

function handleTouchEnd() {
     state.isDragging = false;
     setTimeout(() => {
          state.hasActuallyDragged = false;
     }, 100);
}

function handleMouseDown(e) {
     e.preventDefault();
     state.isDragging = true;
     state.startX = e.clientX;
     state.lastMouseX = e.clientX;
     state.lastX = state.targetX;
     state.dragDistance = 0;
     state.hasActuallyDragged = false;
     state.lastScrollTime = Date.now();
}

function handleMouseMove(e) {
     if (!state.isDragging) return;

     e.preventDefault();
     const deltaX = (e.clientX - state.lastMouseX) * 2;
     state.targetX += deltaX;
     state.lastMouseX = e.clientX;
     state.dragDistance += Math.abs(deltaX);

     if (state.dragDistance > 5) {
          state.hasActuallyDragged = true;
     }

     state.lastScrollTime = Date.now();
}

function handleMouseUp() {
     state.isDragging = false;
     setTimeout(() => {
          state.hasActuallyDragged = false;
     }, 100);
}

function handleResize() {
     initializeSlides();
}

function initializeEventListeners() {
     const slider = document.querySelector(".slider");
     if (!slider) return;

     slider.addEventListener("wheel", handleWheel, { passive: false });
     slider.addEventListener("touchstart", handleTouchStart);
     slider.addEventListener("touchmove", handleTouchMove);
     slider.addEventListener("touchend", handleTouchEnd);
     slider.addEventListener("mousedown", handleMouseDown);
     slider.addEventListener("mouseleave", handleMouseUp);
     slider.addEventListener("dragstart", (e) => e.preventDefault());

     document.addEventListener("mousemove", handleMouseMove);
     document.addEventListener("mouseup", handleMouseUp);
     window.addEventListener("resize", handleResize);
}

async function initializeSlider() {
     await loadMatelotDubeImages();
     initializeSlides();
     initializeEventListeners();
     animate();
     console.log('✅ Horizontal Slider initialized');
}

document.addEventListener("DOMContentLoaded", initializeSlider);

export default {};
